
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TaskAssignmentModal } from "@/components/tasks/TaskAssignmentModal";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { 
  Bot, 
  Loader2, 
  Zap,
  Plus,
  AlertCircle
} from "lucide-react";

interface GeneratedTask {
  title: string;
  description: string;
  status: string;
  priority: string;
  phase_id: string;
  assigned_to?: string;
  start_date?: string;
  due_date?: string;
  order_index: number;
}

interface ProjectAIAssistantProps {
  projectId: string;
  project: any;
  phases: any[];
  tasks: any[];
  onDataChange?: () => void;
}

export const ProjectAIAssistant = ({ 
  projectId, 
  project, 
  phases, 
  tasks,
  onDataChange
}: ProjectAIAssistantProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState<GeneratedTask[]>([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [taskAssignments, setTaskAssignments] = useState<Record<string, string>>({});
  const [isCreatingTasks, setIsCreatingTasks] = useState(false);
  const { toast } = useToast();
  const { session } = useAuth();
  const { currentOrganization } = useOrganization();

  // Debug logging
  useEffect(() => {
    console.log('ProjectAIAssistant - Debug Info:', {
      'project.prd exists': !!project?.prd,
      'project.prd length': project?.prd?.length || 0,
      'phases.length': phases?.length || 0,
      'phases': phases?.map(p => ({ id: p.id, name: p.name })) || [],
      'canGenerateTasks': !!(project?.prd && project.prd.trim() !== "" && phases && phases.length > 0),
      'currentOrganization': currentOrganization?.id,
      'session': !!session?.user?.id
    });
  }, [project, phases, currentOrganization, session]);

  const sanitizeTaskData = (task: any): GeneratedTask | null => {
    // Sanitize dates - convert invalid dates to null
    const sanitizeDate = (dateStr: string | null): string | null => {
      if (!dateStr || dateStr === '1970-01-01' || dateStr === '1970-01-01T00:00:00.000Z') {
        return null;
      }
      return dateStr;
    };

    // Assign first available phase if phase_id is null or invalid
    let phaseId = task.phase_id;
    if (!phaseId || !phases.find(p => p.id === phaseId)) {
      phaseId = phases.length > 0 ? phases[0].id : null;
      console.warn(`Task "${task.title}" had invalid phase_id, assigned to first phase:`, phaseId);
    }

    return {
      title: task.title,
      description: task.description || '',
      status: task.status || 'to_do',
      priority: task.priority || 'medium',
      phase_id: phaseId,
      assigned_to: task.assigned_to || null,
      start_date: sanitizeDate(task.start_date),
      due_date: sanitizeDate(task.due_date),
      order_index: task.order_index || 0
    };
  };

  const validateGeneratedTasks = (tasks: any[]): GeneratedTask[] => {
    console.log('Validating and sanitizing generated tasks:', tasks);
    
    const validPhaseIds = new Set(phases.map(p => p.id));
    console.log('Valid phase IDs:', Array.from(validPhaseIds));
    
    if (phases.length === 0) {
      console.error('No phases available - cannot create tasks');
      return [];
    }
    
    const sanitizedTasks = tasks.map((task, index) => {
      const issues: string[] = [];
      
      if (!task.title || task.title.trim() === '') {
        issues.push('missing or empty title');
        return null;
      }
      
      if (issues.length > 0) {
        console.warn(`Task ${index + 1} validation failed:`, issues, task);
        return null;
      }
      
      return sanitizeTaskData(task);
    }).filter(task => task !== null) as GeneratedTask[];
    
    console.log(`Successfully processed ${sanitizedTasks.length} out of ${tasks.length} tasks`);
    return sanitizedTasks;
  };

  const generateTasks = async () => {
    if (isLoading) return;

    console.log('Generate Tasks - Starting validation...');

    // Validate requirements
    if (!project?.prd || project.prd.trim() === "") {
      console.log('Generate Tasks - No PRD found');
      toast({
        title: "Error",
        description: "Project must have a PRD (Product Requirements Document) to generate tasks.",
        variant: "destructive",
      });
      return;
    }

    if (!phases || phases.length === 0) {
      console.log('Generate Tasks - No phases found');
      toast({
        title: "Error", 
        description: "Project must have phases defined to generate tasks.",
        variant: "destructive",
      });
      return;
    }

    console.log('Generate Tasks - Validation passed, starting generation...');
    setIsLoading(true);

    try {
      // Prepare simplified data for n8n webhook
      const webhookData = {
        project_id: projectId,
        organization_id: currentOrganization?.id,
        fases: phases.map(phase => ({
          id: phase.id,
          nombre: phase.name,
          description: phase.description,
          status: phase.status
        })),
        prd: project.prd
      };

      console.log('Sending webhook data:', webhookData);

      const response = await fetch('https://n8n.laclb.tech/webhook/crm/generate-tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      if (!response.ok) {
        throw new Error(`Webhook error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Webhook response structure:', JSON.stringify(data, null, 2));

      // Process response - handle multiple formats
      let tasks = [];
      
      if (Array.isArray(data)) {
        if (data.length > 0 && data[0] && data[0].tasks && Array.isArray(data[0].tasks)) {
          tasks = data[0].tasks;
          console.log('Found tasks in data[0].tasks format:', tasks.length, 'tasks');
        } else {
          tasks = data;
          console.log('Found direct array of tasks:', tasks.length, 'tasks');
        }
      } else if (data && data.tasks && Array.isArray(data.tasks)) {
        tasks = data.tasks;
        console.log('Found tasks in data.tasks format:', tasks.length, 'tasks');
      } else {
        console.error('Invalid response format. Expected formats:', {
          'Format 1': '[{project_id, organization_id, tasks: [...]}]',
          'Format 2': '{tasks: [...]}', 
          'Format 3': '[...]',
          'Received': typeof data,
          'Data': data
        });
        throw new Error('Invalid response format: expected tasks array in one of the supported formats');
      }

      // Validate and filter tasks
      const validTasks = validateGeneratedTasks(tasks);

      if (validTasks.length === 0) {
        throw new Error('No valid tasks found. All tasks are missing required fields (title or phase_id)');
      }

      console.log(`Setting ${validTasks.length} validated tasks`);
      setGeneratedTasks(validTasks);
      
      toast({
        title: "Success",
        description: `Generated ${validTasks.length} tasks successfully!`,
      });

    } catch (error) {
      console.error('Error generating tasks:', error);
      toast({
        title: "Error",
        description: `Failed to generate tasks: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTasks = () => {
    console.log('Handle Create Tasks - Generated tasks count:', generatedTasks.length);
    if (generatedTasks.length > 0) {
      setShowAssignmentModal(true);
    }
  };

  const handleCreateTasksWithAssignments = async () => {
    console.log('Starting task creation process...');
    console.log('Generated tasks:', generatedTasks);
    console.log('Task assignments:', taskAssignments);
    console.log('Organization ID:', currentOrganization?.id);
    console.log('User ID:', session?.user.id);

    if (!currentOrganization?.id) {
      console.error('No organization ID available');
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }

    if (!session?.user.id) {
      console.error('No user ID available');
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingTasks(true);
    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;
    const errors: string[] = [];
    const successes: string[] = [];
    const skipped: string[] = [];

    try {
      for (let i = 0; i < generatedTasks.length; i++) {
        const task = generatedTasks[i];
        const assignedTo = taskAssignments[i.toString()] || task.assigned_to;
        
        // Skip tasks without valid phase_id after sanitization
        if (!task.phase_id) {
          console.warn(`Skipping task "${task.title}" - no valid phase available`);
          skipped.push(`"${task.title}" - no valid phase`);
          skippedCount++;
          continue;
        }
        
        console.log(`Creating task ${i + 1}/${generatedTasks.length}:`, {
          title: task.title,
          phase_id: task.phase_id,
          assigned_to: assignedTo,
          organization_id: currentOrganization.id
        });

        try {
          const { data, error } = await supabase.functions.invoke('project-tracking-operations', {
            body: {
              action: "createTask",
              projectId,
              organizationId: currentOrganization.id,
              userId: session.user.id,
              taskData: {
                title: task.title,
                description: task.description || '',
                status: task.status || 'to_do',
                priority: task.priority || 'medium',
                phase_id: task.phase_id,
                assigned_to: assignedTo || null,
                start_date: task.start_date || null,
                due_date: task.due_date || null,
                order_index: task.order_index || i
              }
            }
          });

          if (error) {
            console.error(`Error creating task ${i + 1}:`, error);
            errors.push(`"${task.title}": ${error.message || error}`);
            errorCount++;
          } else if (data && data.success) {
            console.log(`Task ${i + 1} created successfully:`, data);
            successes.push(`"${task.title}"`);
            successCount++;
          } else {
            console.error(`Task ${i + 1} creation failed - no success response:`, data);
            errors.push(`"${task.title}": Unexpected response format`);
            errorCount++;
          }
        } catch (taskError) {
          console.error(`Exception creating task ${i + 1}:`, taskError);
          errors.push(`"${task.title}": ${taskError.message || taskError}`);
          errorCount++;
        }
      }

      // Show detailed results
      if (successCount > 0) {
        console.log(`Successfully created ${successCount} tasks:`, successes);
        toast({
          title: "Tasks Created Successfully",
          description: `${successCount} task(s) created and added to the Kanban board`,
        });
      }

      if (skippedCount > 0) {
        console.warn(`Skipped ${skippedCount} tasks:`, skipped);
        toast({
          title: "Some Tasks Skipped",
          description: `${skippedCount} task(s) skipped due to invalid data`,
          variant: "destructive",
        });
      }

      if (errorCount > 0) {
        console.error('Task creation errors:', errors);
        toast({
          title: errorCount === generatedTasks.length ? "All Tasks Failed" : "Some Tasks Failed",
          description: `${errorCount} task(s) failed to create. Check console for details.`,
          variant: "destructive",
        });
      }

      // Clean up and refresh if any tasks were created
      if (successCount > 0) {
        setGeneratedTasks([]);
        setTaskAssignments({});
        setShowAssignmentModal(false);
        setIsOpen(false);
        
        // Refresh the project data
        if (onDataChange) {
          console.log('Calling onDataChange to refresh project data...');
          onDataChange();
        }
      } else if (errorCount === 0 && skippedCount === generatedTasks.length) {
        toast({
          title: "No Tasks Created",
          description: "All tasks were skipped due to invalid data. Please generate tasks again.",
          variant: "destructive",
        });
      }

    } catch (error) {
      console.error('Critical error during task creation:', error);
      toast({
        title: "Critical Error",
        description: "Failed to create tasks due to a system error",
        variant: "destructive",
      });
    } finally {
      setIsCreatingTasks(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getPhaseNameById = (phaseId: string) => {
    const phase = phases?.find(p => p.id === phaseId);
    return phase?.name || 'Unknown Phase';
  };

  const canGenerateTasks = !!(project?.prd && project.prd.trim() !== "" && phases && phases.length > 0);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          size="lg"
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50 bg-secondary border border-border hover:bg-secondary/80"
          data-ai-assistant-trigger
        >
          <Bot className="h-6 w-6 text-secondary-foreground" />
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-[400px] sm:w-[500px] p-0 flex flex-col">
        <SheetHeader className="p-6 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            AI Task Generator
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            Generate tasks automatically for {project?.name || 'your project'}
          </p>
        </SheetHeader>

        <div className="flex-1 p-6 space-y-6">
          {/* Debug Info */}
          <div className="text-xs text-muted-foreground bg-muted/20 p-2 rounded">
            <div>PRD: {project?.prd ? `${project.prd.length} chars` : 'Not set'}</div>
            <div>Phases: {phases?.length || 0}</div>
            <div>Can Generate: {canGenerateTasks ? 'Yes' : 'No'}</div>
          </div>

          {/* Generate Tasks Section */}
          <div className="space-y-4">
            <div className="text-center space-y-2">
              <Zap className="h-12 w-12 mx-auto text-primary opacity-70" />
              <h3 className="text-lg font-semibold">Generate Project Tasks</h3>
              <p className="text-sm text-muted-foreground">
                AI will analyze your PRD and project phases to create relevant tasks
              </p>
            </div>

            {/* Status checks */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                {project?.prd ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    PRD Available ({project.prd.length} chars)
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    PRD Required
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm">
                {phases && phases.length > 0 ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    {phases.length} Phases Defined
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    Phases Required
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={generateTasks}
              disabled={isLoading || !canGenerateTasks}
              className="w-full"
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Tasks...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Generate Tasks
                </>
              )}
            </Button>
            
            {!canGenerateTasks && (
              <p className="text-xs text-muted-foreground text-center">
                Please ensure you have a PRD and at least one phase defined
              </p>
            )}
          </div>

          {/* Generated Tasks Display */}
          {generatedTasks.length > 0 && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-medium">Generated Tasks ({generatedTasks.length})</h4>
                <Button 
                  size="sm" 
                  onClick={handleCreateTasks}
                  disabled={isCreatingTasks}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Create Tasks
                </Button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {generatedTasks.map((task, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-background">
                    <div className="space-y-2">
                      <h5 className="text-sm font-medium">{task.title}</h5>
                      {task.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {task.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        <Badge variant={getPriorityColor(task.priority)} className="text-xs">
                          {task.priority}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {getPhaseNameById(task.phase_id)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>

      {/* Task Assignment Modal */}
      <TaskAssignmentModal
        open={showAssignmentModal}
        onOpenChange={setShowAssignmentModal}
        tasks={generatedTasks}
        onAssignmentsChange={setTaskAssignments}
        onSave={handleCreateTasksWithAssignments}
        isCreating={isCreatingTasks}
      />
    </Sheet>
  );
};
